@file:OptIn(androidx.compose.ui.ExperimentalComposeUiApi::class)

package com.trevorwiebe.timesheet.invoicing.presentation

import androidx.compose.ui.backhandler.BackHandler
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import androidx.compose.material.icons.filled.Check
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.Spacer
import androidx.compose.material3.Card
import androidx.compose.ui.window.Dialog
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.AssistChip
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.LoadSelectionSummaryCard
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimeSheetButton
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.presentation.admin.EditLoadDialog

@Composable
internal fun NewInvoiceContent(state: InvoicingState, onEvent: (InvoicingEvent) -> Unit) {
    val form = state.newInvoice ?: return
    val loads = state.invoiceableLoads
    BackHandler { onEvent(InvoicingEvent.OnDismissNewInvoice) }
    var rateLoad by remember { mutableStateOf<Load?>(null) }

    Scaffold(
        topBar = {
            TopBar(
                title = "New Invoice",
                backIcon = { BackIcon() },
                onBack = { onEvent(InvoicingEvent.OnDismissNewInvoice) },
            )
        },
        bottomBar = {
            ResponsiveWidth {
                Column(Modifier.fillMaxWidth().navigationBarsPadding().padding(12.dp)) {
                    form.error?.let {
                        Text(it, color = MaterialTheme.colorScheme.error, fontSize = 13.sp)
                    }
                    TimeSheetButton(
                        onClick = { onEvent(InvoicingEvent.OnCreateInvoice) },
                        text = "Create draft invoice",
                        modifier = Modifier.fillMaxWidth(),
                        loading = form.isSaving,
                        enabled = state.canCreateInvoice,
                    )
                }
            }
        },
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
            Column(Modifier.fillMaxSize()) {
                Column(Modifier.padding(horizontal = 12.dp, vertical = 8.dp)) {
                    CustomerPicker(state = state, selectedId = form.customerId, onEvent = onEvent)
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        AssistChip(
                            onClick = { onEvent(InvoicingEvent.OnStartDateClick) },
                            label = { Text("From: " + (form.startDate?.let { Util.toFriendlyDate(it) } ?: "Any date")) },
                        )
                        AssistChip(
                            onClick = { onEvent(InvoicingEvent.OnEndDateClick) },
                            label = { Text("To: " + Util.toFriendlyDate(form.endDate)) },
                        )
                        if (form.startDate != null) {
                            TextButton(onClick = { onEvent(InvoicingEvent.OnStartDateSelected(null)) }) {
                                Text("All dates", color = MaterialTheme.colorScheme.tertiary)
                            }
                        }
                    }
                }

                when {
                    form.customerId.isEmpty() -> Message("Choose a customer to see their uninvoiced loads.")
                    loads.isEmpty() -> Message("This customer has no uninvoiced loads in this date range.")
                    else -> {
                        LoadSelectionSummaryCard(
                            summary = "${form.selectedLoadIds.size} of ${loads.size} loads • ${state.newInvoiceTotalFormatted}",
                            notice = state.unbillableNotice,
                            onSelectAll = { onEvent(InvoicingEvent.OnSelectAllLoads) },
                            onClear = { onEvent(InvoicingEvent.OnClearLoadSelection) },
                        )
                        LazyColumn(Modifier.fillMaxSize()) {
                            items(loads, key = { it.load.id }) { item ->
                                InvoiceableLoadRow(
                                    item = item,
                                    checked = item.load.id in form.selectedLoadIds,
                                    onToggle = { onEvent(InvoicingEvent.OnToggleLoad(item.load.id)) },
                                    onOpen = { rateLoad = item.load },
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    rateLoad?.let { EditLoadDialog(it, onDismiss = { rateLoad = null }) }
    NativeDatePicker(
        date = form.startDate ?: form.endDate,
        showDatePicker = form.showStartPicker,
        onDismiss = { onEvent(InvoicingEvent.OnDismissDatePickers) },
        onDateSelected = { onEvent(InvoicingEvent.OnStartDateSelected(it)) },
    )
    NativeDatePicker(
        date = form.endDate,
        showDatePicker = form.showEndPicker,
        onDismiss = { onEvent(InvoicingEvent.OnDismissDatePickers) },
        onDateSelected = { onEvent(InvoicingEvent.OnEndDateSelected(it)) },
    )
}

@Composable
private fun Message(text: String) {
    Box(Modifier.fillMaxSize()) {
        Text(
            text = text,
            modifier = Modifier.align(Alignment.Center).padding(24.dp),
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        )
    }
}

@Composable
private fun CustomerPicker(state: InvoicingState, selectedId: String, onEvent: (InvoicingEvent) -> Unit) {
    var showDialog by remember { mutableStateOf(false) }
    val selected = state.customers.firstOrNull { it.id == selectedId }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 8.dp)
            .clip(RoundedCornerShape(4.dp))
            .border(1.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(4.dp))
            .clickable { showDialog = true }
            .padding(horizontal = 16.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = selected?.let { state.customerName(it) } ?: "Choose a customer",
            color = if (selected == null) MaterialTheme.colorScheme.onSurfaceVariant
            else MaterialTheme.colorScheme.onBackground,
            modifier = Modifier.weight(1f),
        )
        Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null)
    }
    if (showDialog) {
        CustomerDialog(
            state = state,
            selectedId = selectedId,
            onSelect = {
                showDialog = false
                onEvent(InvoicingEvent.OnSelectCustomer(it))
            },
            onDismiss = { showDialog = false },
        )
    }
}

@Composable
private fun CustomerDialog(
    state: InvoicingState,
    selectedId: String,
    onSelect: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    val outstanding = state.outstandingByCustomer
    Dialog(onDismissRequest = onDismiss) {
        Card(modifier = Modifier.responsiveDialogWidth(), shape = RoundedCornerShape(16.dp)) {
            Column(Modifier.padding(top = 20.dp, bottom = 8.dp)) {
                Text(
                    text = "Choose a customer",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(horizontal = 24.dp),
                )
                Spacer(Modifier.height(12.dp))
                HorizontalDivider()
                LazyColumn(Modifier.heightIn(max = 440.dp)) {
                    items(state.sortedCustomers, key = { it.id }) { customer ->
                        val count = outstanding[customer.id] ?: 0
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { onSelect(customer.id) }
                                .padding(horizontal = 24.dp, vertical = 14.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(
                                text = state.customerName(customer),
                                fontSize = 16.sp,
                                fontWeight = if (customer.id == selectedId) FontWeight.SemiBold else null,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                modifier = Modifier.weight(1f),
                            )
                            if (count > 0) {
                                Text(
                                    text = "$count uninvoiced",
                                    fontSize = 13.sp,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                                    modifier = Modifier.padding(start = 12.dp),
                                )
                            }
                            if (customer.id == selectedId) {
                                Icon(
                                    imageVector = Icons.Default.Check,
                                    contentDescription = "Selected",
                                    tint = MaterialTheme.colorScheme.tertiary,
                                    modifier = Modifier.padding(start = 8.dp).size(20.dp),
                                )
                            }
                        }
                    }
                }
                HorizontalDivider()
                Row(Modifier.fillMaxWidth().padding(horizontal = 12.dp), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) {
                        Text("Cancel", color = MaterialTheme.colorScheme.tertiary)
                    }
                }
            }
        }
    }
}

/** The checkbox picks the load for the invoice; the rest of the row opens the load to edit it. */
@Composable
private fun InvoiceableLoadRow(item: InvoiceableLoad, checked: Boolean, onToggle: () -> Unit, onOpen: () -> Unit) {
    val line = item.line
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onOpen)
            .padding(end = 12.dp, top = 2.dp, bottom = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = { onToggle() },
            enabled = item.isBillable,
            colors = CheckboxDefaults.colors(
                checkedColor = MaterialTheme.colorScheme.tertiary,
                checkmarkColor = Color.White,
            ),
        )
        Column(Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = Util.toFriendlyDate(line.date) + if (line.loadNumber.isNotBlank()) "  #${line.loadNumber}" else "",
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f),
                )
                if (item.isBillable) {
                    Text(text = line.totalFormatted, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                } else {
                    Text(text = "Needs a rate", fontSize = 13.sp, color = MaterialTheme.colorScheme.error)
                }
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = "${line.pickup}  to  ${line.drop}",
                    fontSize = 13.sp,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f),
                )
                if (line.po.isNotBlank()) {
                    Text(
                        text = "PO ${line.po}",
                        fontSize = 13.sp,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                        modifier = Modifier.padding(start = 8.dp),
                    )
                }
            }
        }
    }
    HorizontalDivider()
}
